import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, Video, Play, ExternalLink, Upload, Check, Loader2, LayoutDashboard, Settings, TrendingUp, Microscope, Rocket, Info, XCircle, Plus, MessageSquare } from 'lucide-react';
import api from '../../utils/axios';
import getFullUrl from '../../utils/formatUrl';

const WebControl = ({ webContent, setWebContent, handleUpdateWebContent, savingWeb, mode = 'home-cms' }) => {
    const [uploading, setUploading] = useState(false);
    const [allColleges, setAllColleges] = useState([]);

    useEffect(() => {
        const fetchColleges = async () => {
            try {
                const res = await api.get('/colleges/active');
                setAllColleges(res.data.data);
            } catch (err) {
                console.error("Failed to fetch colleges", err);
            }
        };
        fetchColleges();
    }, []);

    // Parse gallery images
    const galleryImages = (() => {
        try {
            return webContent.gallery_images ? JSON.parse(webContent.gallery_images) : [];
        } catch(e) {
            return [];
        }
    })();

    // Parse gallery albums (Categorized: Year -> Event -> Images)
    const galleryAlbums = (() => {
        try {
            const data = webContent.gallery_albums ? JSON.parse(webContent.gallery_albums) : [];
            // Migration: If data is old flat structure, convert it to a default category
            if (data.length > 0 && data[0].title && !data[0].albums) {
                return [{
                    category: 'General',
                    albums: data
                }];
            }
            return data;
        } catch(e) {
            return [];
        }
    })();

    // Parse leadership members
    const leadershipMembers = (() => {
        try {
            return webContent.about_leadership_members ? JSON.parse(webContent.about_leadership_members) : [];
        } catch(e) {
            return [];
        }
    })();

    // Parse Success Stories
    const successStories = (() => {
        try {
            return webContent.placements_success_stories ? JSON.parse(webContent.placements_success_stories) : [];
        } catch(e) {
            return [];
        }
    })();

    // Parse Recruiter Logos
    const recruiterLogos = (() => {
        try {
            return webContent.placements_recruiters ? JSON.parse(webContent.placements_recruiters) : [];
        } catch(e) {
            return [];
        }
    })();

    // Parse Affiliation Logos
    const affiliationLogos = (() => {
        try {
            const data = webContent.home_affiliations ? JSON.parse(webContent.home_affiliations) : [];
            // Migration: Convert old string-only array to object array
            return data.map(item => typeof item === 'string' ? { logo: item, link: '' } : item);
        } catch(e) {
            return [];
        }
    })();




    // Parse Course Cards
    const courseCards = (() => {
        try {
            return webContent.home_course_cards ? JSON.parse(webContent.home_course_cards) : [];
        } catch(e) {
            return [];
        }
    })();



    // Parse Student Spotlight
    const studentSpotlight = (() => {
        try {
            return webContent.home_student_spotlight ? JSON.parse(webContent.home_student_spotlight) : [
                { image: '', name: 'Student 1', role: 'B.Tech CSE' },
                { image: '', name: 'Student 2', role: 'MBA' },
                { image: '', name: 'Student 3', role: 'B.Pharm' }
            ];
        } catch(e) {
            return [
                { image: '', name: 'Student 1', role: 'B.Tech CSE' },
                { image: '', name: 'Student 2', role: 'MBA' },
                { image: '', name: 'Student 3', role: 'B.Pharm' }
            ];
        }
    })();

    const handleCourseCardUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        try {
            const res = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const updated = [...courseCards];
            updated[index].logo = res.data.url;
            setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
        } catch(err) {
            console.error("Course logo upload failed", err);
            alert('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleCourseCardBgUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        try {
            const res = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const updated = [...courseCards];
            updated[index].bgImage = res.data.url;
            setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
        } catch(err) {
            console.error("Course bg upload failed", err);
            alert('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    // Parse Blog Posts
    const blogPosts = (() => {
        try {
            return webContent.blog_posts ? JSON.parse(webContent.blog_posts) : [];
        } catch(e) { return []; }
    })();

    const handleBlogAction = (type, index, field, value) => {
        const updated = [...blogPosts];
        if (type === 'add') {
            updated.push({ title: '', date: new Date().toLocaleDateString(), author: 'Campus Admin', excerpt: '', image: '' });
        } else if (type === 'remove') {
            updated.splice(index, 1);
        } else if (type === 'update') {
            updated[index][field] = value;
        }
        setWebContent({ ...webContent, blog_posts: JSON.stringify(updated) });
    };

    const handleBlogImageUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        try {
            const res = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            handleBlogAction('update', index, 'image', res.data.url);
        } catch(err) {
            console.error("Blog upload failed", err);
            alert('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleFileUpload = async (e, targetField, isArray = false) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            const res = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (isArray) {
                if (targetField === 'about_leadership_members') {
                    // This case is handled via specific functions for members
                    return res.data.url;
                }
                const updatedArray = [...galleryImages, res.data.url];
                setWebContent(prev => ({ ...prev, [targetField]: JSON.stringify(updatedArray) }));
            } else {
                const field = targetField || (webContent.hero_bg_type === 'video' ? 'hero_video' : 'hero_image');
                setWebContent(prev => ({ ...prev, [field]: res.data.url }));
            }
            alert("File uploaded successfully!");
        } catch (err) {
            console.error("Upload failed", err);
            alert("Upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const removeGalleryImage = (index) => {
        const updated = galleryImages.filter((_, i) => i !== index);
        setWebContent(prev => ({ ...prev, gallery_images: JSON.stringify(updated) }));
    };

    const handleMemberAction = (action, index, field, value) => {
        let updated = [...leadershipMembers];
        if (action === 'add') {
            updated.push({ name: '', role: '', image: '' });
        } else if (action === 'update') {
            updated[index][field] = value;
        } else if (action === 'remove') {
            updated.splice(index, 1);
        }
        setWebContent(prev => ({ ...prev, about_leadership_members: JSON.stringify(updated) }));
    };

    const handleMemberImageUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        setUploading(true);
        try {
            const res = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            handleMemberAction('update', index, 'image', res.data.url);
        } catch (err) {
            alert("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleSuccessStoryAction = (action, index, field, value) => {
        let updated = [...successStories];
        if (action === 'add') {
            updated.push({ name: '', company: '', package: '', text: '', image: '' });
        } else if (action === 'update') {
            updated[index][field] = value;
        } else if (action === 'remove') {
            updated.splice(index, 1);
        }
        setWebContent(prev => ({ ...prev, placements_success_stories: JSON.stringify(updated) }));
    };

    const handleSuccessImageUpload = async (e, index) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        try {
            const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            handleSuccessStoryAction('update', index, 'image', res.data.url);
        } catch (err) { alert("Upload failed"); } finally { setUploading(false); }
    };

    const removeRecruiterLogo = (index) => {
        const updated = recruiterLogos.filter((_, i) => i !== index);
        setWebContent(prev => ({ ...prev, placements_recruiters: JSON.stringify(updated) }));
    };

    const handleRecruiterUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        try {
            const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            const updated = [...recruiterLogos, res.data.url];
            setWebContent(prev => ({ ...prev, placements_recruiters: JSON.stringify(updated) }));
        } catch (err) { alert("Upload failed"); } finally { setUploading(false); }
    };

    const removeAffiliationLogo = (index) => {
        const updated = affiliationLogos.filter((_, i) => i !== index);
        setWebContent(prev => ({ ...prev, home_affiliations: JSON.stringify(updated) }));
    };



    const handleAlbumAction = (action, categoryIdx, albumIdx, field, value) => {
        let updated = [...galleryAlbums];
        
        if (action === 'add-category') {
            updated.push({ category: 'NEW YEAR ' + (updated.length + 1), albums: [] });
        } else if (action === 'remove-category') {
            updated.splice(categoryIdx, 1);
        } else if (action === 'update-category') {
            updated[categoryIdx].category = value;
        } else if (action === 'add-album') {
            updated[categoryIdx].albums.push({ title: 'New Event', cover: '', images: [] });
        } else if (action === 'remove-album') {
            updated[categoryIdx].albums.splice(albumIdx, 1);
        } else if (action === 'update-album') {
            updated[categoryIdx].albums[albumIdx][field] = value;
        } else if (action === 'remove-image') {
            const imgIdx = field;
            updated[categoryIdx].albums[albumIdx].images = updated[categoryIdx].albums[albumIdx].images.filter((_, i) => i !== imgIdx);
        }
        
        setWebContent(prev => ({ ...prev, gallery_albums: JSON.stringify(updated) }));
    };

    const handleAlbumImageUpload = async (e, categoryIdx, albumIdx, isCover = false) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        try {
            const res = await api.post('/upload', formData, { 
                headers: { 'Content-Type': 'multipart/form-data' } 
            });
            const url = res.data.url;
            
            let updated = [...galleryAlbums];
            if (isCover) {
                updated[categoryIdx].albums[albumIdx].cover = url;
            } else {
                updated[categoryIdx].albums[albumIdx].images = [...updated[categoryIdx].albums[albumIdx].images, url];
            }
            setWebContent(prev => ({ ...prev, gallery_albums: JSON.stringify(updated) }));
        } catch (err) {
            alert('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleAffiliationUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        try {
            const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            const updated = [...affiliationLogos, { logo: res.data.url, link: '' }];
            setWebContent(prev => ({ ...prev, home_affiliations: JSON.stringify(updated) }));
        } catch (err) { alert("Upload failed"); } finally { setUploading(false); }
    };

    const updateAffiliationLink = (idx, link) => {
        const updated = [...affiliationLogos];
        updated[idx].link = link;
        setWebContent(prev => ({ ...prev, home_affiliations: JSON.stringify(updated) }));
    };
  return (
    <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-10">
      <form onSubmit={handleUpdateWebContent} className="space-y-12">
        
        {/* CONTEXTUAL INTRO BANNERS */}
        {mode === 'home-cms' && (
            <div className="bg-[#003366] text-white p-8 rounded-sm mb-12 relative overflow-hidden">
                <div className="relative z-10">
                    <h4 className="text-2xl font-black uppercase tracking-tighter mb-2">Home Page CMS</h4>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300 max-w-xl">
                        Manage the primary landing experience, including the animated hero banner, news ticker, and global contact details.
                    </p>
                </div>
                <div className="absolute right-[-20px] bottom-[-20px] opacity-10">
                    <LayoutDashboard size={150} />
                </div>
            </div>
        )}

        {mode === 'about-cms' && (
            <div className="bg-amber-600 text-white p-8 rounded-sm mb-12 relative overflow-hidden">
                <div className="relative z-10">
                    <h4 className="text-2xl font-black uppercase tracking-tighter mb-2">About Page Editor</h4>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/80 max-w-xl">
                        Edit the comprehensive "About Us" page. You can customize the page hero, historical heritage, and mission/vision statements.
                    </p>
                </div>
                <div className="absolute right-[-20px] bottom-[-20px] opacity-10">
                    <Settings size={150} />
                </div>
            </div>
        )}

        {mode === 'gallery-cms' && (
            <div className="bg-indigo-900 text-white p-8 rounded-sm mb-12 relative overflow-hidden">
                <div className="relative z-10">
                    <h4 className="text-2xl font-black uppercase tracking-tighter mb-2">Gallery & Slider Hub</h4>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 max-w-xl">
                        Manage the "Moments of Excellence" slider. Upload high-quality campus photos to showcase life at Arya Sabhyata.
                    </p>
                </div>
                <div className="absolute right-[-20px] bottom-[-20px] opacity-10">
                    <ImageIcon size={150} />
                </div>
            </div>
        )}

        {mode === 'placement-page' && (
            <div className="bg-[#cc0000] text-white p-8 rounded-sm mb-12 relative overflow-hidden">
                <div className="relative z-10">
                    <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic underline underline-offset-8">Placement Page Editor</h4>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/80 max-w-xl">
                        Customize the building careers page. Update highest packages, recruiter stats, and industry descriptions.
                    </p>
                </div>
                <div className="absolute right-[-20px] bottom-[-20px] opacity-10">
                    <TrendingUp size={150} />
                </div>
            </div>
        )}

        {mode === 'research-page' && (
            <div className="bg-slate-900 text-white p-8 rounded-sm mb-12 relative overflow-hidden">
                <div className="relative z-10">
                    <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic underline underline-offset-8">Research & Dev Editor</h4>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 max-w-xl">
                        Manage the innovation hub content. Update research paper counts, patents, and core focus areas.
                    </p>
                </div>
                <div className="absolute right-[-20px] bottom-[-20px] opacity-10">
                    <Microscope size={150} />
                </div>
            </div>
        )}

        {mode === 'innovation-page' && (
            <div className="bg-amber-600 text-white p-8 rounded-sm mb-12 relative overflow-hidden">
                <div className="relative z-10">
                    <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic underline underline-offset-8">Innovation Hub Editor</h4>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/80 max-w-xl">
                        Manage the entrepreneurship ecosystem. Update startup counts, industry partners, and tech verticals.
                    </p>
                </div>
                <div className="absolute right-[-20px] bottom-[-20px] opacity-10">
                    <Rocket size={150} />
                </div>
            </div>
        )}

        {mode === 'footer-cms' && (
            <div className="bg-slate-900 text-white p-8 rounded-sm mb-12 relative overflow-hidden">
                <div className="relative z-10">
                    <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic underline underline-offset-8">Footer Management</h4>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 max-w-xl">
                        Customize the global footer. Update social links, institutional links, academic policy links, and site-wide contact info.
                    </p>
                </div>
                <div className="absolute right-[-20px] bottom-[-20px] opacity-10">
                    <Info size={150} />
                </div>
            </div>
        )}

        {/* HOME CMS MODE */}
        {mode === 'home-cms' && (
            <>


                {/* 2. NEWS & TICKER */}
                <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">02</span>
                        News & Announcements Ticker
                    </h4>
                    <div className="bg-slate-50 p-6 rounded-sm border border-slate-100 mb-8">
                        <div className="flex justify-between items-center mb-3">
                            <label className="block text-[10px] font-black uppercase text-slate-500">News Ticker Headline</label>
                            <span className="text-[9px] font-bold text-amber-600 uppercase tracking-tighter">Use "|" pipe to separate multiple news items</span>
                        </div>
                        <textarea 
                            rows="2"
                            className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4 resize-none"
                            value={webContent.ticker_news || 'Admissions open for Engineering, Management, and Basic Science programs for the academic session 2026-27 | Arya Sabhyata Group Ranked #1 for Innovations in Technical Education | Placements 2025: Highest package of 1.2 Crore offered to our student'}
                            placeholder="News 1 | News 2 | News 3"
                            onChange={e => setWebContent({...webContent, ticker_news: e.target.value})}
                        />
                        <p className="mt-2 text-[9px] text-slate-400 font-medium italic">Example: Admission Open 2026 | New Campus Branch Opening | 100% Placement Record</p>
                    </div>

                    {/* Announcement Popup Manager */}
                    <div className="bg-white p-8 border border-slate-200 rounded-sm shadow-sm">
                        <div className="flex items-center justify-between mb-8 border-b border-slate-100 pb-6">
                            <div>
                                <h5 className="text-xs font-black uppercase text-slate-900 tracking-wider">Announcement Popup</h5>
                                <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">This popup will appear when visitors first load the website.</p>
                            </div>
                            <div className="flex items-center space-x-3">
                                <span className={`text-[10px] font-black uppercase ${webContent.popup_enabled === 'true' ? 'text-green-600' : 'text-slate-400'}`}>
                                    {webContent.popup_enabled === 'true' ? 'Active' : 'Disabled'}
                                </span>
                                <button 
                                    type="button"
                                    onClick={() => setWebContent({...webContent, popup_enabled: webContent.popup_enabled === 'true' ? 'false' : 'true'})}
                                    className={`w-14 h-7 rounded-full transition-all relative ${webContent.popup_enabled === 'true' ? 'bg-green-500' : 'bg-slate-200'}`}
                                >
                                    <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${webContent.popup_enabled === 'true' ? 'right-1' : 'left-1 shadow-sm'}`}></div>
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-3 text-left">Popup Title</label>
                                    <input 
                                        type="text" 
                                        className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-slate-50 px-4"
                                        value={webContent.popup_title || ''}
                                        placeholder="Important Notice"
                                        onChange={e => setWebContent({...webContent, popup_title: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-3 text-left">Popup Message</label>
                                    <textarea 
                                        rows="4"
                                        className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-slate-50 px-4 resize-none"
                                        value={webContent.popup_message || ''}
                                        placeholder="Registration for the academic session 2026-27 is now open."
                                        onChange={e => setWebContent({...webContent, popup_message: e.target.value})}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-500 mb-2 text-left">Button Text</label>
                                        <input 
                                            type="text" 
                                            className="w-full border-b border-slate-200 bg-slate-50 py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-xs"
                                            value={webContent.popup_btn_text || ''}
                                            placeholder="Apply Now"
                                            onChange={e => setWebContent({...webContent, popup_btn_text: e.target.value})}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-500 mb-2 text-left">Redirect URL</label>
                                        <input 
                                            type="text" 
                                            className="w-full border-b border-slate-200 bg-slate-50 py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-xs"
                                            value={webContent.popup_link || ''}
                                            placeholder="/apply"
                                            onChange={e => setWebContent({...webContent, popup_link: e.target.value})}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2 text-left">Popup Image (Optional)</label>
                                <div className="aspect-video bg-slate-100 rounded-sm overflow-hidden flex items-center justify-center border-2 border-dashed border-slate-200 relative group">
                                    {webContent.popup_image ? (
                                        <img src={getFullUrl(webContent.popup_image)} className="w-full h-full object-cover" alt="Popup" />
                                    ) : (
                                        <div className="text-center p-6">
                                            <ImageIcon size={32} className="mx-auto text-slate-300 mb-2" />
                                            <p className="text-[10px] font-bold text-slate-400 uppercase">Recommended: 800x600px</p>
                                        </div>
                                    )}
                                    <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                        <Upload className="text-white w-8 h-8" />
                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'popup_image')} />
                                    </label>
                                </div>
                                {webContent.popup_image && (
                                    <button 
                                        type="button"
                                        onClick={() => setWebContent({...webContent, popup_image: ''})}
                                        className="text-[9px] font-black uppercase text-red-500 hover:underline"
                                    >
                                        Remove Image
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3. HERO / BANNER CUSTOMIZATION */}
                <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">03</span>
                        Hero Banner Customization
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 bg-slate-50 p-8 rounded-sm border border-slate-100">
                        <div className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-3">Background Style</label>
                                <div className="flex space-x-4">
                                    <button 
                                        type="button"
                                        onClick={() => setWebContent({...webContent, hero_bg_type: 'image'})}
                                        className={`flex-1 py-4 px-4 flex items-center justify-center space-x-2 border-2 transition-all ${webContent.hero_bg_type !== 'video' ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'}`}
                                    >
                                        <ImageIcon size={18} />
                                        <span className="text-xs font-black uppercase">Static Image</span>
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setWebContent({...webContent, hero_bg_type: 'video'})}
                                        className={`flex-1 py-4 px-4 flex items-center justify-center space-x-2 border-2 transition-all ${webContent.hero_bg_type === 'video' ? 'bg-[#003366] text-white border-[#003366]' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'}`}
                                    >
                                        <Video size={18} />
                                        <span className="text-xs font-black uppercase">Background Video</span>
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">
                                    {webContent.hero_bg_type === 'video' ? 'Hero Background Video' : 'Hero Banner Image'}
                                </label>
                                
                                {/* Media Preview */}
                                <div className="mb-4 aspect-video bg-slate-100 rounded-sm overflow-hidden flex items-center justify-center border border-slate-200">
                                    {webContent.hero_bg_type === 'video' ? (
                                        webContent.hero_video ? (
                                            <video 
                                                src={webContent.hero_video.startsWith('http') ? webContent.hero_video : getFullUrl(webContent.hero_video)} 
                                                className="w-full h-full object-cover" 
                                                controls 
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center text-slate-400">
                                                <Video size={32} className="mb-2" />
                                                <span className="text-[10px] uppercase font-bold">No Video Selected</span>
                                            </div>
                                        )
                                    ) : (
                                        webContent.hero_image ? (
                                            <img 
                                                src={webContent.hero_image.startsWith('http') ? webContent.hero_image : getFullUrl(webContent.hero_image)} 
                                                className="w-full h-full object-cover" 
                                                alt="Hero Preview" 
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center text-slate-400">
                                                <ImageIcon size={32} className="mb-2" />
                                                <span className="text-[10px] uppercase font-bold">No Image Selected</span>
                                            </div>
                                        )
                                    )}
                                </div>

                                <div className="flex items-center space-x-4">
                                    <div className="flex-1 relative">
                                        <input 
                                            type="text" 
                                            className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4 pr-10"
                                            value={webContent.hero_bg_type === 'video' ? (webContent.hero_video || '') : (webContent.hero_image || '')}
                                            placeholder={webContent.hero_bg_type === 'video' ? "No video uploaded" : "No image uploaded"}
                                            readOnly
                                        />
                                        {(webContent.hero_video || webContent.hero_image) && (
                                            <Check className="absolute right-3 top-3 text-green-500 w-4 h-4" />
                                        )}
                                    </div>
                                    <label className={`cursor-pointer flex items-center justify-center px-4 py-3 rounded-sm transition-all shadow-md ${uploading ? 'bg-slate-100 text-slate-400' : 'bg-slate-900 text-white hover:bg-[#cc0000]'}`}>
                                        {uploading ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Upload className="w-4 h-4 mr-2" />
                                        )}
                                        <span className="text-[10px] font-black uppercase tracking-widest">
                                            {uploading ? 'Processing' : 'Upload File'}
                                        </span>
                                        <input 
                                            type="file" 
                                            className="hidden" 
                                            accept={webContent.hero_bg_type === 'video' ? "video/*" : "image/*"}
                                            onChange={(e) => handleFileUpload(e, webContent.hero_bg_type === 'video' ? 'hero_video' : 'hero_image')}
                                            disabled={uploading}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Years Legacy</label>
                                    <input 
                                        type="text" 
                                        className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4"
                                        value={webContent.stats_years || ''}
                                        placeholder="25"
                                        onChange={e => setWebContent({...webContent, stats_years: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Total Alumni</label>
                                    <input 
                                        type="text" 
                                        className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4"
                                        value={webContent.stats_alumni || ''}
                                        placeholder="15K+"
                                        onChange={e => setWebContent({...webContent, stats_alumni: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Campuses</label>
                                    <input 
                                        type="text" 
                                        className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4"
                                        value={webContent.stats_campuses || ''}
                                        placeholder="8+"
                                        onChange={e => setWebContent({...webContent, stats_campuses: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Total Faculty</label>
                                    <input 
                                        type="text" 
                                        className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4"
                                        value={webContent.stats_faculty || ''}
                                        placeholder="500+"
                                        onChange={e => setWebContent({...webContent, stats_faculty: e.target.value})}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Display Badge (e.g. ESTD 1998)</label>
                                <input 
                                    type="text" 
                                    className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4"
                                    value={webContent.hero_badge || ''}
                                    placeholder="ESTD 1998"
                                    onChange={e => setWebContent({...webContent, hero_badge: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Main Headline / Title</label>
                                <input 
                                    type="text" 
                                    className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4"
                                    value={webContent.hero_title || ''}
                                    placeholder="Building Pillars of Life."
                                    onChange={e => setWebContent({...webContent, hero_title: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Hero Description</label>
                                <textarea 
                                    rows="3"
                                    className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-white px-4 resize-none"
                                    value={webContent.hero_description || ''}
                                    placeholder="Arya Sabhyata Group of Institutions..."
                                    onChange={e => setWebContent({...webContent, hero_description: e.target.value})}
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-white border border-slate-100 rounded-sm">
                                    <label className="text-[8px] font-black uppercase text-slate-400 mb-3 flex items-center">
                                        <ExternalLink size={10} className="mr-1" /> Primary Button
                                    </label>
                                    <input 
                                        type="text" 
                                        className="w-full border-b border-slate-100 mb-2 outline-none text-[11px] font-bold"
                                        placeholder="EXPLORE"
                                        value={webContent.hero_btn1_text || ''}
                                        onChange={e => setWebContent({...webContent, hero_btn1_text: e.target.value})}
                                    />
                                </div>
                                <div className="p-4 bg-white border border-slate-100 rounded-sm">
                                    <label className="text-[8px] font-black uppercase text-slate-400 mb-3 flex items-center">
                                        <Play size={10} className="mr-1" /> Video Button
                                    </label>
                                    <input 
                                        type="text" 
                                        className="w-full border-b border-slate-100 mb-2 outline-none text-[11px] font-bold"
                                        placeholder="WATCH"
                                        value={webContent.hero_btn2_text || ''}
                                        onChange={e => setWebContent({...webContent, hero_btn2_text: e.target.value})}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3. HOME ABOUT ENHANCED */}
                <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">03</span>
                        Home About Section Image & Stats
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 bg-slate-50 p-8 rounded-sm border border-slate-100">
                        <div className="space-y-6">
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">About Section Image</label>
                            <div className="flex items-center space-x-4 mb-4">
                                <input type="text" className="flex-1 border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_image || ''} readOnly placeholder="No image uploaded" />
                                <label className={`cursor-pointer px-4 py-3 bg-slate-900 text-white rounded-sm text-[10px] font-black uppercase hover:bg-[#cc0000] transition-all flex items-center ${uploading ? 'opacity-50' : ''}`}>
                                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                    Upload
                                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'about_image')} disabled={uploading} />
                                </label>
                            </div>
                            <textarea rows="3" className="w-full border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm resize-none" value={webContent.about_description || ''} placeholder="Founded with a vision..." onChange={e => setWebContent({...webContent, about_description: e.target.value})} />
                        </div>
                        <div className="space-y-6">
                           <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Placement %</label>
                                    <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_placement || ''} placeholder="95%" onChange={e => setWebContent({...webContent, about_placement: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Industry Ties</label>
                                    <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_industry || ''} placeholder="500+" onChange={e => setWebContent({...webContent, about_industry: e.target.value})} />
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">About Section Main Title</label>
                                    <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_title_main || ''} placeholder="Pioneering The Future of" onChange={e => setWebContent({...webContent, about_title_main: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">About Section Accent Title</label>
                                    <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_title_accent || ''} placeholder="Excellence" onChange={e => setWebContent({...webContent, about_title_accent: e.target.value})} />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 3.5. FOUNDER MESSAGE */}
                <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">3.5</span>
                        Founder's Message / Talk
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 bg-slate-50 p-8 rounded-sm border border-slate-100">
                        <div className="space-y-6">
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Founder Image</label>
                            <div className="flex items-center space-x-6 mb-4">
                                <div className="w-32 h-40 bg-slate-100 flex items-center justify-center rounded-sm border-2 border-slate-200 overflow-hidden">
                                    <img 
                                        src={webContent.founder_image ? getFullUrl(webContent.founder_image) : "https://placehold.co/400x500/003366/ffffff?text=Founder"} 
                                        className="w-full h-full object-cover" 
                                        alt="Founder Preview" 
                                    />
                                </div>
                                <label className={`cursor-pointer px-6 py-3 bg-slate-900 text-white rounded-sm text-[10px] font-black uppercase hover:bg-[#cc0000] transition-all flex items-center ${uploading ? 'opacity-50' : ''}`}>
                                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                    Update Image
                                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'founder_image')} disabled={uploading} />
                                </label>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Founder Name</label>
                                    <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.founder_name || ''} placeholder="Ex: Mr. Prem Agarwal" onChange={e => setWebContent({...webContent, founder_name: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Designation</label>
                                    <textarea rows="2" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm resize-none" value={webContent.founder_role || ''} placeholder="Founder & Chairman" onChange={e => setWebContent({...webContent, founder_role: e.target.value})} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Title Badge</label>
                                <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.founder_title_badge || ''} placeholder="MEET OUR VISIONARY FOUNDER" onChange={e => setWebContent({...webContent, founder_title_badge: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Message / Talk Content</label>
                                <textarea rows="6" className="w-full border-b border-slate-200 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm resize-none leading-relaxed" value={webContent.founder_message || ''} placeholder="Write the founder's message here..." onChange={e => setWebContent({...webContent, founder_message: e.target.value})} />
                            </div>
                        </div>
                    </div>
                </section>

                {/* 4. GLOBAL CONTACT */}

                <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">04</span>
                        Global Contact & Admissions
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 bg-slate-50 p-6 rounded-sm border border-slate-100">
                        <div>
                          <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Contact Email</label>
                          <input type="email" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.contact_email || ''} onChange={e => setWebContent({...webContent, contact_email: e.target.value})} />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Contact Phone</label>
                          <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.contact_phone || ''} onChange={e => setWebContent({...webContent, contact_phone: e.target.value})} />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Apply URL</label>
                          <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.apply_url || ''} onChange={e => setWebContent({...webContent, apply_url: e.target.value})} />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Campus Address</label>
                          <input type="text" className="w-full border-b border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.contact_address || ''} onChange={e => setWebContent({...webContent, contact_address: e.target.value})} />
                        </div>
                    </div>
                </section>

                {/* 5. SUCCESS STORIES MARQUEE */}
                <section>
                    <div className="flex items-center justify-between mb-8 border-b border-slate-100 pb-6">
                        <h4 className="text-sm font-black uppercase text-[#003366] flex items-center">
                            <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">05</span>
                            Student Success Stories (Home Marquee)
                        </h4>
                        <div className="flex items-center space-x-3">
                            <span className={`text-[10px] font-black uppercase ${webContent.placements_marquee_enabled === 'true' ? 'text-green-600' : 'text-slate-400'}`}>
                                {webContent.placements_marquee_enabled === 'true' ? 'Active' : 'Disabled'}
                            </span>
                            <button 
                                type="button"
                                onClick={() => setWebContent({...webContent, placements_marquee_enabled: webContent.placements_marquee_enabled === 'true' ? 'false' : 'true'})}
                                className={`w-14 h-7 rounded-full transition-all relative ${webContent.placements_marquee_enabled === 'true' ? 'bg-green-500' : 'bg-slate-200'}`}
                            >
                                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${webContent.placements_marquee_enabled === 'true' ? 'right-1' : 'left-1 shadow-sm'}`}></div>
                            </button>
                        </div>
                    </div>
                    <div className="bg-white p-8 border border-slate-200 rounded-sm shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                            {successStories.map((story, idx) => (
                                <div key={idx} className="bg-slate-50 p-6 border border-slate-200 rounded-sm relative group shadow-sm transition-all hover:shadow-md">
                                    <button 
                                        type="button"
                                        onClick={() => handleSuccessStoryAction('remove', idx)}
                                        className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <XCircle size={16} />
                                    </button>
                                    <div className="flex gap-6 mb-6">
                                        <div className="w-20 h-20 bg-white border border-slate-200 rounded-full overflow-hidden flex-shrink-0 relative group/img">
                                            {story.image ? (
                                                <img src={getFullUrl(story.image)} className="w-full h-full object-cover" alt="Student" />
                                            ) : (
                                                <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                                    <ImageIcon size={20} />
                                                </div>
                                            )}
                                            <label className="absolute inset-0 bg-black/60 opacity-0 group-hover/img:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                                                <Upload size={14} className="text-white" />
                                                <input type="file" className="hidden" onChange={(e) => handleSuccessImageUpload(e, idx)} />
                                            </label>
                                        </div>
                                        <div className="flex-1 space-y-3">
                                            <div>
                                                <label className="text-[7px] font-black uppercase text-slate-400">Student Name</label>
                                                <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1" value={story.name || ''} onChange={e => handleSuccessStoryAction('update', idx, 'name', e.target.value)} />
                                            </div>
                                            <div className="flex gap-4">
                                                <div className="flex-1">
                                                    <label className="text-[7px] font-black uppercase text-slate-400">Company</label>
                                                    <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1" value={story.company || ''} onChange={e => handleSuccessStoryAction('update', idx, 'company', e.target.value)} />
                                                </div>
                                                <div className="w-20">
                                                    <label className="text-[7px] font-black uppercase text-slate-400">Package (LPA)</label>
                                                    <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1" value={story.package || ''} onChange={e => handleSuccessStoryAction('update', idx, 'package', e.target.value)} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[7px] font-black uppercase text-slate-400">Success Quote / Highlight</label>
                                        <textarea rows="2" className="w-full border border-slate-200 bg-white p-2 text-[10px] font-medium mt-1 outline-none focus:border-[#cc0000]" value={story.text || ''} onChange={e => handleSuccessStoryAction('update', idx, 'text', e.target.value)} />
                                    </div>
                                </div>
                            ))}
                            <button 
                                type="button"
                                onClick={() => handleSuccessStoryAction('add')}
                                className="border-2 border-dashed border-slate-200 rounded-sm flex flex-col items-center justify-center p-8 hover:border-[#cc0000] hover:bg-slate-50 transition-all text-slate-400 hover:text-[#cc0000] group"
                            >
                                <Plus size={24} className="mb-2 transform group-hover:rotate-90 transition-transform" />
                                <span className="text-[9px] font-black uppercase tracking-[2px]">Add New Success Story</span>
                            </button>
                        </div>
                        <p className="text-[10px] text-slate-400 italic font-medium">These stories will appear in the high-speed marquee at the bottom of the home page.</p>
                    </div>
                </section>

                {/* 6. AFFILIATION & ACCREDITATION */}
                <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">06</span>
                        Affiliation & Accreditation
                    </h4>
                    <div className="bg-white p-8 border border-slate-200 rounded-sm">
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {affiliationLogos.map((item, idx) => (
                                <div key={idx} className="space-y-2">
                                    <div className="relative group aspect-video bg-slate-50 border border-slate-200 rounded-sm overflow-hidden p-4">
                                        <img src={getFullUrl(item.logo)} className="w-full h-full object-contain" alt="Affiliation" />
                                        <button type="button" onClick={() => removeAffiliationLogo(idx)} className="absolute inset-0 bg-red-600/90 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-black text-[10px] uppercase tracking-tighter">
                                            Remove
                                        </button>
                                    </div>
                                    <input 
                                        type="text" 
                                        className="w-full border-b border-slate-200 py-1 text-[9px] font-bold outline-none focus:border-[#cc0000] uppercase tracking-widest placeholder:text-slate-300"
                                        placeholder="Redirect Link"
                                        value={item.link || ''}
                                        onChange={e => updateAffiliationLink(idx, e.target.value)}
                                    />
                                </div>
                            ))}
                            <label className={`cursor-pointer aspect-video border-2 border-dashed border-slate-200 rounded-sm flex flex-col items-center justify-center transition-all hover:border-[#cc0000] hover:bg-slate-50 ${uploading ? 'opacity-50' : ''}`}>
                                <Upload className="w-6 h-6 text-slate-400 mb-2" />
                                <span className="text-[8px] font-black uppercase text-slate-400">Add Logo</span>
                                <input type="file" className="hidden" accept="image/*" onChange={handleAffiliationUpload} disabled={uploading} />
                            </label>
                        </div>
                    </div>
                </section>

                {/* 7. OUR PROMINENT RECRUITERS */}
                <section>
                    <div className="flex items-center justify-between mb-8 border-b border-slate-100 pb-6">
                        <h4 className="text-sm font-black uppercase text-[#003366] flex items-center">
                            <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">07</span>
                            Our Prominent Recruiters (Home)
                        </h4>
                        <div className="flex items-center space-x-3">
                            <span className={`text-[10px] font-black uppercase ${webContent.home_recruiters_enabled === 'true' ? 'text-green-600' : 'text-slate-400'}`}>
                                {webContent.home_recruiters_enabled === 'true' ? 'Active' : 'Disabled'}
                            </span>
                            <button 
                                type="button"
                                onClick={() => setWebContent({...webContent, home_recruiters_enabled: webContent.home_recruiters_enabled === 'true' ? 'false' : 'true'})}
                                className={`w-14 h-7 rounded-full transition-all relative ${webContent.home_recruiters_enabled === 'true' ? 'bg-green-500' : 'bg-slate-200'}`}
                            >
                                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${webContent.home_recruiters_enabled === 'true' ? 'right-1' : 'left-1 shadow-sm'}`}></div>
                            </button>
                        </div>
                    </div>
                    <div className="bg-slate-900 p-8 border border-slate-800 rounded-sm">
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            {recruiterLogos.map((logo, idx) => (
                                <div key={idx} className="relative group aspect-video bg-white border border-slate-200 rounded-sm overflow-hidden p-4 flex items-center justify-center shadow-lg hover:shadow-2xl transition-all duration-500">
                                    <img src={getFullUrl(logo)} className="w-[80%] h-[80%] object-contain transform group-hover:scale-110 transition-transform" alt="Recruiter" />
                                    <button type="button" onClick={() => removeRecruiterLogo(idx)} className="absolute inset-0 bg-red-600/90 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-black text-[10px] uppercase tracking-tighter">
                                        Remove Logo
                                    </button>
                                </div>
                            ))}
                            <label className={`cursor-pointer aspect-video border-2 border-dashed border-white/20 rounded-sm flex flex-col items-center justify-center transition-all hover:border-[#cc0000] hover:bg-white/5 ${uploading ? 'opacity-50' : ''}`}>
                                <Upload className="w-6 h-6 text-white/40 mb-2" />
                                <span className="text-[8px] font-black uppercase text-white/40">Add Logo</span>
                                <input type="file" className="hidden" accept="image/*" onChange={handleRecruiterUpload} disabled={uploading} />
                            </label>
                        </div>
                    </div>
                </section>



                {/* 9. COURSE CARDS (Choose Your Future Path) */}
                <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">09</span>
                        Course Cards (Choose Your Future Path)
                    </h4>
                    <div className="bg-slate-900 p-8 border border-slate-800 rounded-sm">
                        <p className="text-[10px] text-slate-400 italic font-medium mb-6">Manage the course category cards displayed on the homepage under "Choose Your Future Path". Each card shows a title, description, and icon. Leave empty to use defaults (Technical Education, Management & Business, Computer Applications).</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                            {courseCards.map((card, idx) => (
                                <div key={idx} className="bg-white/5 border border-white/10 rounded-sm p-6 relative group">
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            const updated = courseCards.filter((_, i) => i !== idx);
                                            setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
                                        }}
                                        className="absolute top-2 right-2 text-red-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <XCircle size={16} />
                                    </button>
                                    <div className="flex justify-between items-center border-b border-white/10 pb-3 mb-4">
                                        <span className="text-[9px] font-black uppercase text-slate-400">Course Card {idx + 1}</span>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[8px] font-black uppercase text-slate-400 mb-1">Title</label>
                                            <input 
                                                type="text" 
                                                className="w-full border-b border-white/20 bg-transparent text-white py-2 outline-none focus:border-[#cc0000] font-bold text-xs"
                                                value={card.title || ''}
                                                placeholder="e.g. Technical Education"
                                                onChange={e => {
                                                    const updated = [...courseCards];
                                                    updated[idx].title = e.target.value;
                                                    setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[8px] font-black uppercase text-slate-400 mb-1">Description</label>
                                            <textarea 
                                                rows="2"
                                                className="w-full border-b border-white/20 bg-transparent text-white py-2 outline-none focus:border-[#cc0000] font-bold text-xs resize-none leading-relaxed"
                                                value={card.desc || ''}
                                                placeholder="e.g. B.Tech programs in 12+ specializations..."
                                                onChange={e => {
                                                    const updated = [...courseCards];
                                                    updated[idx].desc = e.target.value;
                                                    setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
                                                }}
                                            />
                                        </div>
                                        <div className="flex gap-4 items-start">
                                            <div className="flex-1">
                                                <label className="block text-[8px] font-black uppercase text-slate-400 mb-1">Associated Colleges</label>
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {(card.colleges || (card.college ? [card.college] : [])).map((colName, cIdx) => (
                                                        <div key={cIdx} className="bg-[#cc0000]/20 text-[#cc0000] text-[9px] font-black uppercase px-2 py-1 rounded-sm border border-[#cc0000]/30 flex items-center">
                                                            {colName}
                                                            <button 
                                                                onClick={() => {
                                                                    const currentCols = card.colleges || (card.college ? [card.college] : []);
                                                                    const updatedCols = currentCols.filter((_, i) => i !== cIdx);
                                                                    const updated = [...courseCards];
                                                                    updated[idx].colleges = updatedCols;
                                                                    delete updated[idx].college; // Clean up old field
                                                                    setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
                                                                }}
                                                                className="ml-2 hover:text-white"
                                                            >
                                                                <XCircle size={10} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                <select 
                                                    className="w-full border-b border-white/20 bg-slate-800 text-white py-2 outline-none focus:border-[#cc0000] font-bold text-xs"
                                                    value=""
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        if (!val) return;
                                                        const currentCols = card.colleges || (card.college ? [card.college] : []);
                                                        if (currentCols.includes(val)) return;
                                                        const updatedCols = [...currentCols, val];
                                                        const updated = [...courseCards];
                                                        updated[idx].colleges = updatedCols;
                                                        delete updated[idx].college; // Clean up old field
                                                        setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
                                                    }}
                                                >
                                                    <option value="">+ Add More Colleges</option>
                                                    {allColleges.map((c, i) => (
                                                        <option key={i} value={c.name}>{c.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="w-20">
                                                <label className="block text-[8px] font-black uppercase text-slate-400 mb-1">Logo</label>
                                                <div className="relative aspect-square bg-slate-800 rounded-sm overflow-hidden flex items-center justify-center border border-white/10 group/logo">
                                                    {card.logo ? (
                                                        <img src={getFullUrl(card.logo)} className="w-full h-full object-contain p-2" alt="Logo" />
                                                    ) : (
                                                        <ImageIcon size={16} className="text-white/20" />
                                                    )}
                                                    <label className="absolute inset-0 bg-black/60 opacity-0 group-hover/logo:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                                                        <Upload size={12} className="text-white" />
                                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleCourseCardUpload(e, idx)} />
                                                    </label>
                                                </div>
                                                {card.logo && (
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            const updated = [...courseCards];
                                                            updated[idx].logo = '';
                                                            setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
                                                        }}
                                                        className="text-[7px] text-red-400 uppercase font-bold block mt-1 hover:underline"
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>

                                            <div className="flex-1">
                                                <label className="block text-[8px] font-black uppercase text-slate-400 mb-1 tracking-widest">Background Image (Optional)</label>
                                                <div className="relative aspect-video bg-slate-800 rounded-sm overflow-hidden flex items-center justify-center border border-white/10 group/bg">
                                                    {card.bgImage ? (
                                                        <img src={getFullUrl(card.bgImage)} className="w-full h-full object-cover" alt="Background" />
                                                    ) : (
                                                        <div className="text-center">
                                                            <ImageIcon size={16} className="text-white/20 mx-auto" />
                                                            <span className="text-[7px] text-white/20 block mt-1 uppercase">Card BG</span>
                                                        </div>
                                                    )}
                                                    <label className="absolute inset-0 bg-black/60 opacity-0 group-hover/bg:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                                                        <Upload size={14} className="text-white" />
                                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleCourseCardBgUpload(e, idx)} />
                                                    </label>
                                                </div>
                                                {card.bgImage && (
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            const updated = [...courseCards];
                                                            updated[idx].bgImage = '';
                                                            setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
                                                        }}
                                                        className="text-[7px] text-red-400 uppercase font-bold block mt-1 hover:underline text-left"
                                                    >
                                                        Remove Background
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button 
                                type="button"
                                onClick={() => {
                                    const updated = [...courseCards, { title: '', desc: '', icon: 'BookOpen' }];
                                    setWebContent(prev => ({ ...prev, home_course_cards: JSON.stringify(updated) }));
                                }}
                                className="border-2 border-dashed border-white/20 rounded-sm flex flex-col items-center justify-center py-10 hover:border-[#cc0000] hover:bg-white/5 transition-all group"
                            >
                                <Plus className="text-white/30 group-hover:text-[#cc0000] mb-2" />
                                <span className="text-[10px] font-black uppercase text-white/30 group-hover:text-[#cc0000]">Add Course Card</span>
                            </button>
                        </div>
                    </div>
                </section>

                {/* 10. HOME GALLERY PREVIEW */}
                <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">10</span>
                        Gallery & Moments Preview
                    </h4>
                    <div className="bg-white p-8 border border-slate-200 rounded-sm">
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                            {galleryImages.slice(0, 11).map((img, idx) => (
                                <div key={idx} className="relative group aspect-square bg-slate-50 border border-slate-200 rounded-sm overflow-hidden">
                                    <img src={img.startsWith('http') ? img : getFullUrl(img)} className="w-full h-full object-cover" alt="Gallery" />
                                    <button type="button" onClick={() => removeGalleryImage(idx)} className="absolute inset-0 bg-red-600/90 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-black text-[10px] uppercase tracking-tighter">
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <label className={`cursor-pointer aspect-square border-2 border-dashed border-slate-200 rounded-sm flex flex-col items-center justify-center transition-all hover:border-[#cc0000] hover:bg-slate-50 ${uploading ? 'opacity-50' : ''}`}>
                                <Upload className="w-6 h-6 text-slate-400 mb-2" />
                                <span className="text-[8px] font-black uppercase text-slate-400">Add Photo</span>
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'gallery_images', true)} disabled={uploading} />
                            </label>
                        </div>
                        <p className="text-[10px] text-slate-400 italic font-medium">This gallery is shared with the main Gallery Slider page. You can manage full moments there.</p>
                    </div>
                </section>

                {/* 11. STUDENT SPOTLIGHT (3 IMAGES) */}
                {/* <section>
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                        <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">11</span>
                        Student Spotlight (3-Image Grid)
                    </h4>
                    <div className="bg-slate-50 p-8 border border-slate-100 rounded-sm">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {studentSpotlight.map((student, idx) => (
                                <div key={idx} className="bg-white p-6 border border-slate-200 rounded-sm shadow-sm space-y-6 relative group">
                                    <div className="relative aspect-[4/5] bg-slate-100 rounded-sm overflow-hidden border-2 border-slate-100 group-hover:border-[#cc0000] transition-colors">
                                        {student.image ? (
                                            <img src={getFullUrl(student.image)} className="w-full h-full object-cover" alt={`Student ${idx+1}`} />
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                                <ImageIcon size={48} />
                                                <span className="text-[10px] font-black uppercase mt-2">No Image</span>
                                            </div>
                                        )}
                                        <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center cursor-pointer">
                                            <Upload className="text-white mb-2" size={24} />
                                            <span className="text-white text-[10px] font-black uppercase tracking-widest">Update Portrait</span>
                                            <input type="file" className="hidden" accept="image/*" onChange={(e) => handleStudentSpotlightUpload(e, idx)} />
                                        </label>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[8px] font-black uppercase text-slate-400 mb-1 tracking-[2px]">Student Name</label>
                                            <input 
                                                type="text" 
                                                className="w-full border-b border-slate-100 py-2 outline-none focus:border-[#cc0000] font-bold text-sm"
                                                value={student.name || ''}
                                                placeholder="Enter Name"
                                                onChange={e => updateStudentSpotlight(idx, 'name', e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[8px] font-black uppercase text-slate-400 mb-1 tracking-[2px]">Program / Role</label>
                                            <input 
                                                type="text" 
                                                className="w-full border-b border-slate-100 py-2 outline-none focus:border-[#cc0000] font-bold text-sm"
                                                value={student.role || ''}
                                                placeholder="e.g. B.Tech Computer Science"
                                                onChange={e => updateStudentSpotlight(idx, 'role', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-6 text-[10px] text-slate-400 font-medium italic">This grid will showcase 3 featured students on the home page with high-impact visuals.</p>
                    </div>
                </section> */}

            </>
        )}

        {/* BLOG CMS MODE */}
        {mode === 'blog-cms' && (
            <>
                <div className="bg-slate-900 text-white p-8 rounded-sm mb-12 relative overflow-hidden">
                    <div className="relative z-10">
                        <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic underline underline-offset-8">Campus Blog & Journal Editor</h4>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 max-w-xl">
                            Create and manage the campus stories, news, and academic journals. Control article visibility and featured content.
                        </p>
                    </div>
                    <div className="absolute right-[-20px] bottom-[-20px] opacity-10">
                        <MessageSquare size={150} />
                    </div>
                </div>

                <section>
                    <div className="flex items-center justify-between mb-8 border-b border-slate-100 pb-6">
                        <h4 className="text-sm font-black uppercase text-[#003366] flex items-center">
                            <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">01</span>
                            Campus Blog & Stories
                        </h4>
                        <div className="flex items-center space-x-3">
                            <span className={`text-[10px] font-black uppercase ${webContent.blog_enabled === 'true' ? 'text-green-600' : 'text-slate-400'}`}>
                                {webContent.blog_enabled === 'true' ? 'Active' : 'Disabled'}
                            </span>
                            <button 
                                type="button" 
                                onClick={() => setWebContent({...webContent, blog_enabled: webContent.blog_enabled === 'true' ? 'false' : 'true'})}
                                className={`w-14 h-7 rounded-full transition-all relative ${webContent.blog_enabled === 'true' ? 'bg-green-500' : 'bg-slate-200'}`}
                            >
                                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${webContent.blog_enabled === 'true' ? 'right-1' : 'left-1 shadow-sm'}`}></div>
                            </button>
                        </div>
                    </div>
                    
                    <div className="bg-white p-8 border border-slate-200 rounded-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                            {blogPosts.map((post, idx) => (
                                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-sm p-6 relative group border-t-4 border-t-[#cc0000]/20 hover:border-t-[#cc0000] transition-colors">
                                    <button 
                                        type="button" 
                                        onClick={() => handleBlogAction('remove', idx)}
                                        className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <XCircle size={16} />
                                    </button>
                                    
                                    <div className="aspect-[4/3] bg-white border border-slate-200 rounded-sm overflow-hidden mb-4 relative group/img">
                                        {post.image ? (
                                            <img src={getFullUrl(post.image)} className="w-full h-full object-cover" alt="Blog" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon size={24} /></div>
                                        )}
                                        <label className="absolute inset-0 bg-black/60 opacity-0 group-hover/img:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                                            <Upload size={16} className="text-white" />
                                            <input type="file" className="hidden" accept="image/*" onChange={(e) => handleBlogImageUpload(e, idx)} />
                                        </label>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-[7px] font-black uppercase text-slate-400">Article Title</label>
                                            <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1 outline-none focus:border-[#cc0000]" value={post.title || ''} onChange={e => handleBlogAction('update', idx, 'title', e.target.value)} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[7px] font-black uppercase text-slate-400">Date</label>
                                                <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1 outline-none focus:border-[#cc0000]" value={post.date || ''} onChange={e => handleBlogAction('update', idx, 'date', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="text-[7px] font-black uppercase text-slate-400">Author</label>
                                                <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1 outline-none focus:border-[#cc0000]" value={post.author || ''} onChange={e => handleBlogAction('update', idx, 'author', e.target.value)} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[7px] font-black uppercase text-slate-400">Excerpt / Summary</label>
                                            <textarea rows="3" className="w-full border border-slate-200 bg-white p-2 text-[10px] font-medium outline-none focus:border-[#cc0000]" value={post.excerpt || ''} onChange={e => handleBlogAction('update', idx, 'excerpt', e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button 
                                type="button"
                                onClick={() => handleBlogAction('add')}
                                className="border-2 border-dashed border-slate-200 rounded-sm flex flex-col items-center justify-center py-12 hover:border-[#cc0000] hover:bg-slate-50 transition-all text-slate-400 hover:text-[#cc0000] group min-h-[300px]"
                            >
                                <Plus size={32} className="mb-2 transform group-hover:rotate-90 transition-transform" />
                                <span className="text-[10px] font-black uppercase tracking-widest">Add New Article</span>
                            </button>
                        </div>
                    </div>
                </section>
            </>
        )}

        {/* ABOUT CMS MODE */}
        {mode === 'about-cms' && (
            <section>
                <h4 className="text-sm font-black uppercase text-[#003366] mb-6 flex items-center">
                    <span className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mr-3 text-xs">01</span>
                    About Page Comprehensive Setup
                </h4>
                <div className="bg-slate-50 p-8 rounded-sm border border-slate-100 space-y-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">About Page Hero Title</label>
                            <input type="text" className="w-full border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_hero_title || ''} onChange={e => setWebContent({...webContent, about_hero_title: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">About Page Hero Image</label>
                            <div className="flex items-center space-x-4">
                                <input type="text" className="flex-1 border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_hero_image || ''} readOnly />
                                <label className={`cursor-pointer px-4 py-3 bg-slate-900 text-white rounded-sm text-[10px] font-black uppercase hover:bg-[#cc0000] transition-all flex items-center ${uploading ? 'opacity-50' : ''}`}>
                                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                    Upload
                                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'about_hero_image')} disabled={uploading} />
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-200">
                        <div className="space-y-4">
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Heritage Heading</label>
                            <input type="text" className="w-full border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_heritage_heading || ''} onChange={e => setWebContent({...webContent, about_heritage_heading: e.target.value})} />
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Heritage Description</label>
                            <textarea rows="4" className="w-full border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm resize-none" value={webContent.about_heritage_description || ''} onChange={e => setWebContent({...webContent, about_heritage_description: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Heritage Image</label>
                            <div className="flex items-center space-x-4 mb-4">
                                <input type="text" className="flex-1 border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.about_heritage_image || ''} readOnly />
                                <label className={`cursor-pointer px-4 py-3 bg-slate-900 text-white rounded-sm text-[10px] font-black uppercase hover:bg-[#cc0000] transition-all flex items-center ${uploading ? 'opacity-50' : ''}`}>
                                    <Upload className="w-4 h-4 mr-2" /> Upload
                                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, 'about_heritage_image')} disabled={uploading} />
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-200">
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Mission Statement</label>
                            <textarea rows="4" className="w-full border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm resize-none" value={webContent.about_mission || ''} onChange={e => setWebContent({...webContent, about_mission: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Vision Statement</label>
                            <textarea rows="4" className="w-full border-b-2 border-slate-100 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm resize-none" value={webContent.about_vision || ''} onChange={e => setWebContent({...webContent, about_vision: e.target.value})} />
                        </div>
                    </div>

                    {/* Leadership Setup */}
                    <div className="pt-10 border-t-4 border-[#cc0000]">
                        <h4 className="text-sm font-black uppercase text-[#003366] mb-8">Board of Governors / Leadership Team</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {leadershipMembers.map((member, idx) => (
                                <div key={idx} className="bg-white p-6 border border-slate-200 rounded-sm relative shadow-sm">
                                    <button 
                                        type="button"
                                        onClick={() => handleMemberAction('remove', idx)}
                                        className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1 rounded-sm"
                                    >
                                        <XCircle size={16} />
                                    </button>
                                    <div className="grid grid-cols-3 gap-6">
                                        <div className="col-span-1">
                                            <div className="aspect-[3/4] bg-slate-100 rounded-sm overflow-hidden mb-2 relative group">
                                                {member.image ? (
                                                    <img src={getFullUrl(member.image)} className="w-full h-full object-cover" alt="Member" />
                                                ) : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                                                        <ImageIcon size={24} />
                                                        <span className="text-[8px] font-black uppercase mt-1">No Image</span>
                                                    </div>
                                                )}
                                                <label className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                                                    <Upload size={16} className="text-white" />
                                                    <input type="file" className="hidden" accept="image/*" onChange={(e) => handleMemberImageUpload(e, idx)} disabled={uploading} />
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-span-2 space-y-4">
                                            <div>
                                                <label className="block text-[8px] font-black uppercase text-slate-400 mb-1">Full Name</label>
                                                <input type="text" className="w-full border-b border-slate-200 outline-none text-xs font-bold py-1 focus:border-[#cc0000]" value={member.name || ''} onChange={e => handleMemberAction('update', idx, 'name', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-[8px] font-black uppercase text-slate-400 mb-1">Designation / Role</label>
                                                <input type="text" className="w-full border-b border-slate-200 outline-none text-xs font-bold py-1 focus:border-[#cc0000]" value={member.role || ''} onChange={e => handleMemberAction('update', idx, 'role', e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button 
                                type="button"
                                onClick={() => handleMemberAction('add')}
                                className="border-2 border-dashed border-slate-200 rounded-sm flex flex-col items-center justify-center p-8 hover:border-[#cc0000] hover:bg-slate-50 transition-all text-slate-400 hover:text-[#cc0000]"
                            >
                                <Plus size={24} className="mb-2" />
                                <span className="text-[10px] font-black uppercase tracking-widest">Add Board Member</span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        )}

        {/* PLACEMENT PAGE MODE */}
        {mode === 'placement-page' && (
            <section className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="bg-slate-50 p-8 border border-slate-200">
                    <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-8">Section 1: Hero & Statistics</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-4 tracking-tighter">Hero Background Image</label>
                            <div className="aspect-video bg-slate-200 rounded-sm relative group overflow-hidden border border-slate-300">
                                {webContent.placements_hero_image ? (
                                    <img src={getFullUrl(webContent.placements_hero_image)} className="w-full h-full object-cover" alt="Placement Hero" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-400 uppercase font-bold text-xs">No Image Selected</div>
                                )}
                                <label className="absolute inset-0 bg-[#003366]/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer">
                                    <Upload className="text-white mr-2" size={20} />
                                    <span className="text-white font-black uppercase text-[10px]">Change Background</span>
                                    <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'placements_hero_image')} disabled={uploading} />
                                </label>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Highest Package</label>
                                    <input type="text" className="w-full border-b-2 border-slate-200 bg-transparent py-2 outline-none focus:border-[#cc0000] font-bold text-lg" value={webContent.placements_highest || ''} onChange={e => setWebContent({...webContent, placements_highest: e.target.value})} placeholder="e.g. 1.2 Cr" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Avg Package</label>
                                    <input type="text" className="w-full border-b-2 border-slate-200 bg-transparent py-2 outline-none focus:border-[#cc0000] font-bold text-lg" value={webContent.placements_avg || ''} onChange={e => setWebContent({...webContent, placements_avg: e.target.value})} placeholder="e.g. 8.5 LPA" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Offers Made</label>
                                    <input type="text" className="w-full border-b-2 border-slate-200 bg-transparent py-2 outline-none focus:border-[#cc0000] font-bold text-lg" value={webContent.placements_offers || ''} onChange={e => setWebContent({...webContent, placements_offers: e.target.value})} placeholder="e.g. 2500+" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Recruiters Count</label>
                                    <input type="text" className="w-full border-b-2 border-slate-200 bg-transparent py-2 outline-none focus:border-[#cc0000] font-bold text-lg" value={webContent.placements_recruiters_count || ''} onChange={e => setWebContent({...webContent, placements_recruiters_count: e.target.value})} placeholder="e.g. 500+" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-8 border border-slate-100">
                        <label className="block text-[10px] font-black uppercase text-slate-500 mb-4 tracking-tighter italic">About Placement Cell</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
                            <div>
                                <label className="block text-[8px] font-black uppercase text-slate-400 mb-2">Recruitment Title</label>
                                <input type="text" className="w-full border-b border-slate-200 py-3 outline-none focus:border-[#cc0000] font-black text-2xl uppercase tracking-tighter" value={webContent.placements_title || ''} onChange={e => setWebContent({...webContent, placements_title: e.target.value})} />
                                <label className="block text-[8px] font-black uppercase text-slate-400 mt-6 mb-2">Main Description</label>
                                <textarea rows="6" className="w-full border border-slate-200 p-4 outline-none focus:border-[#cc0000] font-bold text-sm leading-relaxed" value={webContent.placements_desc || ''} onChange={e => setWebContent({...webContent, placements_desc: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-4 tracking-tighter italic">Side Showcase Image</label>
                                <div className="aspect-[4/5] bg-slate-100 rounded-sm overflow-hidden border border-slate-200 relative group">
                                    {webContent.placements_side_image ? (
                                        <img src={getFullUrl(webContent.placements_side_image)} className="w-full h-full object-cover" alt="Side" />
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center opacity-20">
                                            <ImageIcon size={64} />
                                        </div>
                                    )}
                                    <label className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity cursor-pointer text-white">
                                        <Upload size={32} />
                                        <span className="text-[10px] font-black uppercase mt-2">Replace Showcase Image</span>
                                        <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'placements_side_image')} disabled={uploading} />
                                    </label>
                                </div>
                            </div>
                        </div>
                </div>

                {/* Top Recruiters Manager */}
                <div className="bg-slate-900 text-white p-8">
                    <h4 className="text-[10px] font-black uppercase text-white/40 tracking-widest mb-6 italic underline decoration-[#cc0000] decoration-2 underline-offset-8">Top Recruiter Logos</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {recruiterLogos.map((logo, idx) => (
                            <div key={idx} className="relative group aspect-video bg-white border border-slate-200 rounded-sm overflow-hidden p-3">
                                <img src={getFullUrl(logo)} className="w-full h-full object-contain" alt="Recruiter" />
                                <button type="button" onClick={() => removeRecruiterLogo(idx)} className="absolute inset-0 bg-red-600/90 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-black text-[10px] uppercase tracking-tighter">
                                    Remove Logo
                                </button>
                            </div>
                        ))}
                        <label className={`cursor-pointer aspect-video border-2 border-dashed border-white/20 rounded-sm flex flex-col items-center justify-center transition-all hover:border-[#cc0000] hover:bg-white/5 ${uploading ? 'opacity-50' : ''}`}>
                            <Upload className="w-6 h-6 text-white/40 mb-2" />
                            <span className="text-[8px] font-black uppercase text-white/40">Add Company Logo</span>
                            <input type="file" className="hidden" accept="image/*" onChange={handleRecruiterUpload} disabled={uploading} />
                        </label>
                    </div>
                </div>

                {/* Success Stories Manager */}
                <div className="bg-white p-8 border border-slate-200">
                    <h4 className="text-sm font-black uppercase text-[#003366] mb-8 flex items-center">
                        <TrendingUp size={18} className="mr-3 text-[#cc0000]" /> Student Success Stories (Testimonials)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {successStories.map((story, idx) => (
                            <div key={idx} className="bg-slate-50 p-6 border border-slate-200 rounded-sm relative group shadow-sm">
                                <button 
                                    type="button"
                                    onClick={() => handleSuccessStoryAction('remove', idx)}
                                    className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <XCircle size={16} />
                                </button>
                                <div className="flex gap-6 mb-6">
                                    <div className="w-24 h-24 bg-white border border-slate-200 rounded-full overflow-hidden flex-shrink-0 relative group/img">
                                        {story.image ? (
                                            <img src={getFullUrl(story.image)} className="w-full h-full object-cover" alt="Student" />
                                        ) : (
                                            <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                                <ImageIcon size={20} />
                                            </div>
                                        )}
                                        <label className="absolute inset-0 bg-black/60 opacity-0 group-hover/img:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                                            <Upload size={14} className="text-white" />
                                            <input type="file" className="hidden" onChange={(e) => handleSuccessImageUpload(e, idx)} />
                                        </label>
                                    </div>
                                    <div className="flex-1 space-y-3">
                                        <div>
                                            <label className="text-[7px] font-black uppercase text-slate-400">Student Name</label>
                                            <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1" value={story.name || ''} onChange={e => handleSuccessStoryAction('update', idx, 'name', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-[7px] font-black uppercase text-slate-400">Placed At (Company)</label>
                                            <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1" value={story.company || ''} onChange={e => handleSuccessStoryAction('update', idx, 'company', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="text-[7px] font-black uppercase text-slate-400">Package (LPA)</label>
                                            <input type="text" className="w-full border-b border-slate-200 bg-transparent text-xs font-bold py-1" value={story.package || ''} onChange={e => handleSuccessStoryAction('update', idx, 'package', e.target.value)} />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[7px] font-black uppercase text-slate-400">Success Quote / Story</label>
                                    <textarea rows="3" className="w-full border border-slate-200 bg-white p-3 text-[11px] font-medium mt-1 outline-none focus:border-[#cc0000]" value={story.text || ''} onChange={e => handleSuccessStoryAction('update', idx, 'text', e.target.value)} />
                                </div>
                            </div>
                        ))}
                        <button 
                            type="button"
                            onClick={() => handleSuccessStoryAction('add')}
                            className="border-2 border-dashed border-slate-200 rounded-sm flex flex-col items-center justify-center p-12 hover:border-[#cc0000] hover:bg-slate-50 transition-all text-slate-400 hover:text-[#cc0000] group"
                        >
                            <Plus size={32} className="mb-3 transform group-hover:rotate-90 transition-transform" />
                            <span className="text-[10px] font-black uppercase tracking-[3px]">Add New Success Story</span>
                        </button>
                    </div>
                </div>
            </section>
        )}

        {/* RESEARCH PAGE MODE */}
        {mode === 'research-page' && (
            <section className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="bg-slate-900 text-white p-8 rounded-sm">
                    <h4 className="text-[10px] font-black uppercase text-white/40 tracking-widest mb-8 italic italic-offset-8 underline">Section 1: Hero & Real-time Impact</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div>
                            <label className="block text-[10px] font-black uppercase text-white/50 mb-3">Hero Poster</label>
                            <div className="aspect-video bg-white/5 border border-white/10 rounded-sm overflow-hidden relative group">
                                {webContent.research_hero_image ? (
                                    <img src={getFullUrl(webContent.research_hero_image)} className="w-full h-full object-cover" alt="Research Hero" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-white/20 font-black uppercase">No Image</div>
                                )}
                                <label className="absolute inset-0 bg-slate-900/80 p-4 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white">
                                    <Upload size={32} className="mb-2" />
                                    <span className="text-[10px] font-black uppercase">Upload New Hub Background</span>
                                    <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'research_hero_image')} disabled={uploading} />
                                </label>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[8px] font-black uppercase text-amber-400 mb-1">Publications</label>
                                    <input type="text" className="w-full bg-white/5 border-b border-white/20 py-2 outline-none focus:border-amber-400 font-bold text-xl px-2" value={webContent.research_papers || ''} onChange={e => setWebContent({...webContent, research_papers: e.target.value})} placeholder="e.g. 1500+" />
                                </div>
                                <div>
                                    <label className="block text-[8px] font-black uppercase text-amber-400 mb-1">Patents Filed</label>
                                    <input type="text" className="w-full bg-white/5 border-b border-white/20 py-2 outline-none focus:border-amber-400 font-bold text-xl px-2" value={webContent.research_patents || ''} onChange={e => setWebContent({...webContent, research_patents: e.target.value})} placeholder="e.g. 45+" />
                                </div>
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[8px] font-black uppercase text-[#cc0000] mb-1">Funding Grants</label>
                                    <input type="text" className="w-full bg-white/5 border-b border-white/20 py-2 outline-none focus:border-[#cc0000] font-bold text-xl px-2" value={webContent.research_grants || ''} onChange={e => setWebContent({...webContent, research_grants: e.target.value})} placeholder="e.g. 12 Cr+" />
                                </div>
                                <div>
                                    <label className="block text-[8px] font-black uppercase text-[#cc0000] mb-1">Active Centers</label>
                                    <input type="text" className="w-full bg-white/5 border-b border-white/20 py-2 outline-none focus:border-[#cc0000] font-bold text-xl px-2" value={webContent.research_centers_count || ''} onChange={e => setWebContent({...webContent, research_centers_count: e.target.value})} placeholder="e.g. 12" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-8 border border-slate-200 bg-white">
                        <div className="flex flex-col lg:flex-row gap-12">
                            <div className="lg:w-2/3 space-y-8">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Introduction Heading</label>
                                    <input type="text" className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-slate-900 font-black text-3xl uppercase tracking-tighter" value={webContent.research_intro_title || ''} onChange={e => setWebContent({...webContent, research_intro_title: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Research Culture Description</label>
                                    <textarea rows="8" className="w-full border-2 border-slate-50 p-6 outline-none focus:border-[#003366] font-medium text-slate-600 leading-relaxed text-lg" value={webContent.research_intro_desc || ''} onChange={e => setWebContent({...webContent, research_intro_desc: e.target.value})} />
                                </div>
                            </div>
                            <div className="lg:w-1/3">
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-4 tracking-tighter italic">Showcase Image</label>
                                <div className="aspect-square bg-slate-100 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center relative group overflow-hidden">
                                    {webContent.research_intro_image ? (
                                        <img src={getFullUrl(webContent.research_intro_image)} className="w-full h-full object-cover" alt="Research Showcase" />
                                    ) : (
                                        <div className="text-slate-300 flex flex-col items-center">
                                            <Microscope size={48} />
                                            <span className="text-[10px] font-black mt-2">No Image</span>
                                        </div>
                                    )}
                                    <label className="absolute inset-0 bg-[#003366]/40 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity cursor-pointer text-white">
                                        <Upload size={24} />
                                        <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'research_intro_image')} disabled={uploading} />
                                    </label>
                                </div>
                            </div>
                        </div>
                </div>
            </section>
        )}

        {/* INNOVATION PAGE MODE */}
        {mode === 'innovation-page' && (
            <section className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="bg-amber-50 p-8 border border-amber-200">
                    <h4 className="text-[10px] font-black uppercase text-amber-600 tracking-widest mb-8">Section 1: Hero & Ecosystem Impact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-4 tracking-tighter">Hero Background Image</label>
                            <div className="aspect-video bg-slate-200 rounded-sm relative group overflow-hidden border border-slate-300">
                                {webContent.innovations_hero_image ? (
                                    <img src={getFullUrl(webContent.innovations_hero_image)} className="w-full h-full object-cover" alt="Innovation Hero" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-400 uppercase font-bold text-xs">No Image Selected</div>
                                )}
                                <label className="absolute inset-0 bg-[#003366]/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer">
                                    <Upload className="text-white mr-2" size={20} />
                                    <span className="text-white font-black uppercase text-[10px]">Change Background</span>
                                    <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'innovations_hero_image')} disabled={uploading} />
                                </label>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-amber-600 mb-2">Startups Incubated</label>
                                    <input type="text" className="w-full border-b-2 border-amber-200 bg-transparent py-2 outline-none focus:border-[#cc0000] font-bold text-lg" value={webContent.innovations_startups || ''} onChange={e => setWebContent({...webContent, innovations_startups: e.target.value})} placeholder="e.g. 20+" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-amber-600 mb-2">Tech Prototypes</label>
                                    <input type="text" className="w-full border-b-2 border-amber-200 bg-transparent py-2 outline-none focus:border-[#cc0000] font-bold text-lg" value={webContent.innovations_prototypes || ''} onChange={e => setWebContent({...webContent, innovations_prototypes: e.target.value})} placeholder="e.g. 120+" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-[#003366] mb-2">Industry Partners</label>
                                    <input type="text" className="w-full border-b-2 border-[#003366]/20 bg-transparent py-2 outline-none focus:border-[#003366] font-bold text-lg" value={webContent.innovations_partners || ''} onChange={e => setWebContent({...webContent, innovations_partners: e.target.value})} placeholder="e.g. 80+" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-[#003366] mb-2">Awards Won</label>
                                    <input type="text" className="w-full border-b-2 border-[#003366]/20 bg-transparent py-2 outline-none focus:border-[#003366] font-bold text-lg" value={webContent.innovations_awards || ''} onChange={e => setWebContent({...webContent, innovations_awards: e.target.value})} placeholder="e.g. 15+" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-8 border border-slate-100">
                        <label className="block text-[10px] font-black uppercase text-slate-500 mb-4 tracking-tighter italic">About Innovation Hub</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
                            <div>
                                <label className="block text-[8px] font-black uppercase text-slate-400 mb-2">Section Title</label>
                                <input type="text" className="w-full border-b border-slate-200 py-3 outline-none focus:border-amber-600 font-black text-2xl uppercase tracking-tighter" value={webContent.innovations_intro_title || ''} onChange={e => setWebContent({...webContent, innovations_intro_title: e.target.value})} />
                                <label className="block text-[8px] font-black uppercase text-slate-400 mt-6 mb-2">Main Description</label>
                                <textarea rows="6" className="w-full border border-slate-200 p-4 outline-none focus:border-amber-600 font-bold text-sm leading-relaxed" value={webContent.innovations_intro_desc || ''} onChange={e => setWebContent({...webContent, innovations_intro_desc: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-4 tracking-tighter italic">Intro Showcase Image</label>
                                <div className="aspect-video bg-slate-100 rounded-sm overflow-hidden border border-slate-200 relative group">
                                    {webContent.innovations_intro_image ? (
                                        <img src={getFullUrl(webContent.innovations_intro_image)} className="w-full h-full object-cover" alt="Intro" />
                                    ) : (
                                        <div className="w-full h-full flex flex-col items-center justify-center opacity-20">
                                            <Rocket size={64} />
                                        </div>
                                    )}
                                    <label className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity cursor-pointer text-white">
                                        <Upload size={32} />
                                        <span className="text-[10px] font-black uppercase mt-2">Replace Intro Image</span>
                                        <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, 'innovations_intro_image')} disabled={uploading} />
                                    </label>
                                </div>
                            </div>
                        </div>
                </div>
            </section>
        )}

        {/* STUDENT DESK CMS MODE */}
        {mode === 'student-desk-cms' && (
            <section className="animate-in fade-in slide-in-from-bottom-4 duration-700">


            </section>
        )}

        {/* CONTACT PAGE CMS MODE */}
        {mode === 'contact-cms' && (
            <section className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="bg-[#cc0000] text-white p-10 rounded-sm shadow-2xl">
                    <h4 className="text-3xl font-black uppercase tracking-tighter mb-2 italic">Contact Page Manager</h4>
                    <p className="text-[10px] font-bold uppercase tracking-[4px] text-white/70">Institutional Inquiry & Reach</p>
                </div>

                <div className="bg-white p-10 border border-slate-200">
                    <h4 className="text-sm font-black uppercase text-slate-900 mb-10 border-b border-slate-100 pb-4">Core Contact Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="space-y-8">
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Official Email Address</label>
                                <input type="email" className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-lg" value={webContent.contact_email || ''} onChange={e => setWebContent({...webContent, contact_email: e.target.value})} placeholder="info@aryacollege.in" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Official Phone Number</label>
                                <input type="text" className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-lg" value={webContent.contact_phone || ''} onChange={e => setWebContent({...webContent, contact_phone: e.target.value})} placeholder="+91 91234 56789" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Google Maps Embed URL</label>
                                <input type="text" className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-xs" value={webContent.contact_map || ''} onChange={e => setWebContent({...webContent, contact_map: e.target.value})} placeholder="Paste the iframe src URL here..." />
                                <p className="text-[9px] font-bold text-slate-400 mt-2 uppercase">Share → Embed a map → Extract only the 'src' attribute value.</p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Main Campus Address</label>
                            <textarea rows="6" className="w-full border-2 border-slate-50 p-6 outline-none focus:border-[#cc0000] font-bold text-sm leading-relaxed" value={webContent.contact_address || ''} onChange={e => setWebContent({...webContent, contact_address: e.target.value})} placeholder="SP-40, RIICO Industrial Area, Kukas, Jaipur, Rajasthan" />
                        </div>
                    </div>
                </div>
            </section>
        )}

        {/* GALLERY CMS MODE */}
        {mode === 'gallery-cms' && (
            <section className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="bg-slate-900 text-white p-10 rounded-sm shadow-2xl relative overflow-hidden">
                    <div className="relative z-10">
                        <h4 className="text-3xl font-black uppercase tracking-tighter mb-2 italic">Institutional Gallery Manager</h4>
                        <p className="text-[10px] font-bold uppercase tracking-[4px] text-white/50">Categorized Archives: Year → Event → Images</p>
                    </div>
                </div>

                <div className="space-y-20">
                    {galleryAlbums.map((cat, cIdx) => (
                        <div key={cIdx} className="space-y-8 bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <div className="flex items-center justify-between border-b-4 border-slate-900 pb-4">
                                <div className="flex-1">
                                    <label className="block text-[8px] font-black uppercase text-slate-400 mb-1 tracking-[4px]">Category / Year</label>
                                    <input 
                                        type="text" 
                                        className="w-full bg-transparent border-none outline-none font-black text-4xl uppercase tracking-tighter text-[#cc0000]"
                                        value={cat.category || ''}
                                        onChange={e => handleAlbumAction('update-category', cIdx, null, null, e.target.value)}
                                        placeholder="e.g. 2021"
                                    />
                                </div>
                                <button 
                                    type="button"
                                    onClick={() => handleAlbumAction('remove-category', cIdx)}
                                    className="text-red-500 hover:bg-red-50 p-2 rounded-sm font-black text-[10px] uppercase tracking-widest flex items-center"
                                >
                                    <XCircle size={16} className="mr-2" /> Delete Year
                                </button>
                            </div>

                            <div className="grid grid-cols-1 gap-12">
                                {cat.albums?.map((album, aIdx) => (
                                    <div key={aIdx} className="bg-white border border-slate-200 p-8 shadow-sm relative group rounded-sm">
                                        <button 
                                            type="button"
                                            onClick={() => handleAlbumAction('remove-album', cIdx, aIdx)}
                                            className="absolute top-4 right-4 text-red-500 hover:bg-red-50 p-2 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Remove Event Album"
                                        >
                                            <XCircle size={20} />
                                        </button>

                                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
                                            {/* Album Info & Cover */}
                                            <div className="lg:col-span-1 space-y-6">
                                                <div className="relative aspect-[3/4] bg-slate-100 rounded-sm overflow-hidden border-2 border-slate-100 group-hover:border-[#cc0000] transition-colors shadow-sm">
                                                    {album.cover ? (
                                                        <img src={getFullUrl(album.cover)} className="w-full h-full object-cover" alt="Cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                                            <ImageIcon size={40} />
                                                            <span className="text-[10px] font-black uppercase mt-2 text-center px-4">No Cover Image</span>
                                                        </div>
                                                    )}
                                                    <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center cursor-pointer transition-opacity">
                                                        <Upload className="text-white mb-2" size={24} />
                                                        <span className="text-white text-[10px] font-black uppercase tracking-widest text-center px-4">Set Event Thumbnail</span>
                                                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleAlbumImageUpload(e, cIdx, aIdx, true)} />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="block text-[8px] font-black uppercase text-slate-400 mb-2 tracking-widest">Event / Subcategory Name</label>
                                                    <input 
                                                        type="text" 
                                                        className="w-full border-b-2 border-slate-200 py-3 outline-none focus:border-[#cc0000] font-black text-xl uppercase tracking-tighter"
                                                        value={album.title || ''}
                                                        onChange={e => handleAlbumAction('update-album', cIdx, aIdx, 'title', e.target.value)}
                                                        placeholder="e.g. Brainware"
                                                    />
                                                </div>
                                            </div>

                                            {/* Album Images */}
                                            <div className="lg:col-span-3">
                                                <div className="bg-slate-50 p-6 rounded-sm border border-slate-100">
                                                    <h5 className="text-[10px] font-black uppercase text-slate-500 mb-6 flex items-center tracking-[2px]">
                                                        <ImageIcon size={14} className="mr-2 text-[#cc0000]" /> Gallery Photos for {album.title || 'this event'}
                                                    </h5>
                                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                                        {(album.images || []).map((img, iIdx) => (
                                                            <div key={iIdx} className="relative aspect-square bg-white border border-slate-200 rounded-sm overflow-hidden group/img shadow-sm hover:shadow-md transition-shadow">
                                                                <img src={getFullUrl(img)} className="w-full h-full object-cover" alt="Gallery" />
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => handleAlbumAction('remove-image', cIdx, aIdx, iIdx)}
                                                                    className="absolute inset-0 bg-red-600/90 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center text-white font-black text-[10px] uppercase tracking-tighter"
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        ))}
                                                        <label className={`cursor-pointer aspect-square border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center transition-all hover:border-[#cc0000] hover:bg-white hover:shadow-inner ${uploading ? 'opacity-50' : ''}`}>
                                                            <Upload className="w-6 h-6 text-slate-400 mb-2" />
                                                            <span className="text-[8px] font-black uppercase text-slate-500">Add Photo</span>
                                                            <input type="file" className="hidden" accept="image/*" onChange={(e) => handleAlbumImageUpload(e, cIdx, aIdx)} disabled={uploading} />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <button 
                                    type="button"
                                    onClick={() => handleAlbumAction('add-album', cIdx)}
                                    className="border-2 border-dashed border-[#cc0000]/30 rounded-sm py-8 flex flex-col items-center justify-center hover:bg-[#cc0000]/5 transition-all text-[#cc0000]/50 hover:text-[#cc0000] group"
                                >
                                    <Plus size={24} className="mb-2" />
                                    <span className="text-[10px] font-black uppercase tracking-[4px]">Add New Event in {cat.category}</span>
                                </button>
                            </div>
                        </div>
                    ))}

                    <button 
                        type="button"
                        onClick={() => handleAlbumAction('add-category')}
                        className="w-full border-4 border-dashed border-slate-300 rounded-sm py-16 flex flex-col items-center justify-center hover:border-slate-900 hover:bg-slate-50 transition-all text-slate-400 hover:text-slate-900 group shadow-lg"
                    >
                        <Plus size={48} className="mb-4 transform group-hover:rotate-90 transition-transform" />
                        <span className="text-xl font-black uppercase tracking-[8px]">New Year Category</span>
                        <p className="text-[10px] font-bold uppercase mt-2 tracking-widest text-slate-400">Add a new folder for a specific year or major category</p>
                    </button>
                </div>
            </section>
        )}

        {/* FOOTER CMS MODE */}
        {mode === 'footer-cms' && (
            <section className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="bg-slate-50 p-8 border border-slate-200">
                    <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-8">Institutional Description & Social</h4>
                    <div className="space-y-6">
                        <div>
                            <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Short Description</label>
                            <textarea 
                                rows="3" 
                                className="w-full border-b-2 border-slate-200 bg-white py-3 px-4 outline-none focus:border-[#cc0000] font-bold text-sm resize-none" 
                                value={webContent.footer_desc || ''} 
                                onChange={e => setWebContent({...webContent, footer_desc: e.target.value})}
                                placeholder="Arya Sabhyata Group is a leading provider..."
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Facebook URL</label>
                                <input type="text" className="w-full border-b-2 border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.footer_facebook || ''} onChange={e => setWebContent({...webContent, footer_facebook: e.target.value})} placeholder="https://facebook.com/..." />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Twitter URL</label>
                                <input type="text" className="w-full border-b-2 border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.footer_twitter || ''} onChange={e => setWebContent({...webContent, footer_twitter: e.target.value})} placeholder="https://twitter.com/..." />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Instagram URL</label>
                                <input type="text" className="w-full border-b-2 border-slate-200 bg-white py-2 px-3 outline-none focus:border-[#cc0000] font-bold text-sm" value={webContent.footer_instagram || ''} onChange={e => setWebContent({...webContent, footer_instagram: e.target.value})} placeholder="https://instagram.com/..." />
                            </div>
                        </div>
                    </div>
                </div>

                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-white p-8 border border-slate-100 shadow-sm md:col-span-2">
                        <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-6 italic italic-offset-4 underline decoration-[#cc0000] decoration-2">Academic Links</h4>
                        <div className="space-y-4">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="flex space-x-2">
                                    <input 
                                        type="text" 
                                        className="flex-1 border-b border-slate-200 py-2 outline-none focus:border-[#cc0000] text-xs font-bold" 
                                        placeholder={`Link ${i} Label`} 
                                        value={webContent[`footer_acad_link${i}_text`] || ''} 
                                        onChange={e => setWebContent({...webContent, [`footer_acad_link${i}_text`]: e.target.value})}
                                    />
                                    <input 
                                        type="text" 
                                        className="flex-1 border-b border-slate-200 py-2 outline-none focus:border-[#cc0000] text-xs font-bold opacity-60" 
                                        placeholder={`URL ${i}`} 
                                        value={webContent[`footer_acad_link${i}_url`] || ''} 
                                        onChange={e => setWebContent({...webContent, [`footer_acad_link${i}_url`]: e.target.value})}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div> */}

                <div className="bg-slate-900 text-white p-8 rounded-sm">
                    <h4 className="text-[10px] font-black uppercase text-white/40 tracking-widest mb-8">Reach Us Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div>
                            <label className="block text-[8px] font-black uppercase text-[#cc0000] mb-2 tracking-widest">Office Address</label>
                            <textarea rows="3" className="w-full bg-white/5 border border-white/10 p-3 outline-none focus:border-[#cc0000] font-bold text-xs resize-none" value={webContent.contact_address || ''} onChange={e => setWebContent({...webContent, contact_address: e.target.value})} />
                        </div>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-[8px] font-black uppercase text-[#cc0000] mb-2 tracking-widest">Primary Phone</label>
                                <input type="text" className="w-full bg-white/5 border border-white/10 p-3 outline-none focus:border-[#cc0000] font-bold text-xs" value={webContent.contact_phone || ''} onChange={e => setWebContent({...webContent, contact_phone: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-[8px] font-black uppercase text-[#cc0000] mb-2 tracking-widest">Admissions Email</label>
                                <input type="text" className="w-full bg-white/5 border border-white/10 p-3 outline-none focus:border-[#cc0000] font-bold text-xs" value={webContent.contact_email || ''} onChange={e => setWebContent({...webContent, contact_email: e.target.value})} />
                            </div>
                        </div>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-[8px] font-black uppercase text-amber-500 mb-2 tracking-widest">Copyright Text</label>
                                <input type="text" className="w-full bg-white/5 border border-white/10 p-3 outline-none focus:border-amber-500 font-bold text-xs" value={webContent.footer_copyright || ''} onChange={e => setWebContent({...webContent, footer_copyright: e.target.value})} placeholder="© 2026 Arya Sabhyata Group..." />
                            </div>
                            <div>
                                <label className="block text-[8px] font-black uppercase text-amber-500 mb-2 tracking-widest">Managed By Text</label>
                                <input type="text" className="w-full bg-white/5 border border-white/10 p-3 outline-none focus:border-amber-500 font-bold text-xs" value={webContent.footer_managed_by || ''} onChange={e => setWebContent({...webContent, footer_managed_by: e.target.value})} placeholder="Managed by Palit Education Trust" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        )}

        {/* WHATSAPP CMS MODE */}
        {mode === 'whatsapp-cms' && (
            <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="bg-[#25D366] text-white p-10 rounded-sm shadow-2xl relative overflow-hidden">
                    <div className="relative z-10">
                        <h4 className="text-3xl font-black uppercase tracking-tighter mb-2 italic">WhatsApp Integration</h4>
                        <p className="text-[10px] font-bold uppercase tracking-[4px] text-white/60">Configure Floating Communication Hub</p>
                    </div>
                </div>

                <div className="bg-white p-8 border border-slate-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-3 tracking-widest leading-none italic">WhatsApp Number</label>
                                <input 
                                    type="text" 
                                    className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#25D366] font-black text-2xl tracking-tighter" 
                                    value={webContent.whatsapp_number || ''} 
                                    onChange={e => setWebContent({...webContent, whatsapp_number: e.target.value})} 
                                    placeholder="919876543210" 
                                />
                                <p className="text-[8px] font-black text-slate-400 mt-2 uppercase tracking-tight italic">Include country code (e.g. 91) without '+' or space.</p>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase text-slate-500 mb-3 tracking-widest leading-none italic">Pre-filled Response Message</label>
                                <textarea 
                                    rows="6" 
                                    className="w-full bg-slate-50 border border-slate-100 p-5 outline-none focus:border-[#25D366] font-bold text-sm leading-relaxed" 
                                    value={webContent.whatsapp_message || ''} 
                                    onChange={e => setWebContent({...webContent, whatsapp_message: e.target.value})} 
                                    placeholder="Hi! I want to know more about..." 
                                />
                                <p className="text-[8px] font-black text-slate-400 mt-2 uppercase tracking-tight italic">This message will appear in the user's chat box.</p>
                            </div>
                        </div>
                        <div className="bg-[#f0fff4] border border-[#25D366]/20 p-8 flex flex-col items-center justify-center text-center">
                            <div className="w-20 h-20 bg-[#25D366] rounded-full flex items-center justify-center text-white mb-6 shadow-xl shadow-[#25D366]/20 animate-pulse">
                                <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            </div>
                            <h6 className="text-[10px] font-black uppercase tracking-widest text-[#25D366] mb-2">Live Preview Available</h6>
                            <p className="text-slate-500 font-bold text-[10px] leading-relaxed max-w-[200px] uppercase tracking-tighter">Your Floating Action Button will automatically use these settings across the entire platform.</p>
                        </div>
                    </div>
                </div>
            </div>
        )}


        {mode !== 'student-desk-cms' && (
          <div className="pt-10 border-t border-slate-100">
            <button 
              type="submit" 
              disabled={savingWeb}
              className="w-full md:w-auto bg-slate-900 text-white px-16 py-5 font-black uppercase text-xs tracking-[5px] shadow-2xl hover:bg-[#cc0000] transition-all disabled:opacity-50"
            >
              {savingWeb ? 'PUBLISHING CHANGES...' : 
               mode === 'home-cms' ? 'PUBLISH HOME PAGE UPDATES' : 
               mode === 'about-cms' ? 'PUBLISH ABOUT PAGE CHANGES' : 
               mode === 'placement-page' ? 'PUBLISH PLACEMENT PAGE' :
               mode === 'research-page' ? 'PUBLISH RESEARCH PAGE' :
               mode === 'innovation-page' ? 'PUBLISH INNOVATION PAGE' :
               mode === 'student-desk-cms' ? 'PUBLISH STUDENT DESK' :
               mode === 'contact-cms' ? 'PUBLISH CONTACT PAGE' :
               mode === 'footer-cms' ? 'PUBLISH FOOTER UPDATES' :
               mode === 'whatsapp-cms' ? 'PUBLISH WHATSAPP SETTINGS' :
               'SAVE GALLERY UPDATES'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default WebControl;
